import { IFullWidthCopyFields } from '@/types/contentful';
import { Asset } from 'contentful';
import Image from 'next/legacy/image';
import React, { useEffect } from 'react';
import { getImageData } from '~/utils/contentful';
import RichText from '../RichText';
import { useInView } from 'react-intersection-observer';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { useSprings, animated, easings } from '@react-spring/web';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { imageLoader } from '../Image';
import { TextLink, ArrowIcon } from '../AtomicComponents/TextLink';
import Link from '../Link';

export default function FullwidthCopy({
  header,
  variant,
  subheaderCopy,
  backgroundColor,
  cornerRounding,
  rebusImages,
  image,
  linkUrl,
  linkText,
  buttonCtaText
}: IFullWidthCopyFields) {
  const [springs, api] = useSprings(
    rebusImages && !image ? rebusImages.length : 0,
    () => ({
      x: 0,
      config: {
        tension: 100,
        friction: 15,
        easing: easings.easeOutBack
      }
    })
  );

  const isMobile = !useMediaQuery('desktop');

  const [ref, inView] = useInView({
    triggerOnce: true, // Ensures the animation only triggers the first time the element comes into view
    threshold: isMobile ? 0.5 : 0.8
  });

  useEffect(() => {
    if (rebusImages?.length && !image && inView) {
      // Add a 1-second delay before triggering the animation
      const timeout = setTimeout(() => {
        api.start((index) => {
          if (index === 0) {
            return { x: -60 }; // First image slides left (with slight overlap)
          } else if (index === rebusImages.length - 1) {
            return { x: 60 }; // Last image slides right (with slight overlap)
          }
          return { x: 0 }; // Middle images stay in place
        });
      }); // 1 second delay

      return () => clearTimeout(timeout);
    }
  }, [rebusImages, image, api, inView]);

  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <p className="typography-body-1 whitespace-pre-wrap break-words text-center !text-[20px] font-[350] !leading-[28px]">
        {children}
      </p>
    )
  };
  // Full Width Content //
  return (
    <div className="mx-6" ref={ref}>
      <div
        className="flex w-full items-center justify-center gap-4 px-6 py-16 tablet:px-4xl"
        style={{
          borderRadius: cornerRounding ? '16px' : '0',
          backgroundColor: backgroundColor
        }}
      >
        {/** Content */}
        <div className="flex flex-col items-center gap-6 self-stretch tablet:max-w-[644px]">
          {image ? (
            <div className="flex h-[26px] items-center justify-center">
              <Image
                {...getImageData(image)}
                loader={imageLoader}
                width={100}
                height={26}
                layout="fixed"
                className="h-[26px] max-h-[26px] w-auto object-contain"
              />
            </div>
          ) : (
            rebusImages?.length && (
              <div
                className="relative flex items-center"
                style={{
                  marginRight:
                    rebusImages && rebusImages.length > 0
                      ? `calc(-12px * ${(rebusImages.length - 1) / 2})`
                      : 0
                }}
              >
                {springs?.map((style, index) => (
                  <animated.div
                    key={index}
                    className={'size-20'}
                    style={{
                      zIndex: index,
                      position: 'relative',
                      marginLeft: index === 0 ? 0 : '-82px',
                      transform: style.x.to((x) => `translateX(${x}px)`)
                    }}
                  >
                    <Image
                      {...getImageData(rebusImages[index] as Asset)}
                      loader={imageLoader}
                      width={80}
                      height={80}
                      className="size-20 rounded-[80px]"
                      style={{
                        borderColor: backgroundColor,
                        backgroundColor: backgroundColor
                      }}
                    />
                  </animated.div>
                ))}
              </div>
            )
          )}
          <h2
            className={
              variant
                ? 'typography-display-2 self-stretch text-center font-glReproDisplay text-[32px] leading-10 tablet:text-5xl tablet:leading-[3.5rem]'
                : 'typography-display-3 self-stretch text-center font-glReproDisplay text-[28px] leading-9 tablet:text-4xl'
            }
          >
            {header}
          </h2>
          <RichText data={subheaderCopy} overrideOptions={RICH_TEXT_OVERRIDE} />
          {buttonCtaText && linkUrl && (
            <div className="flex w-full flex-grow flex-col items-center justify-center">
              <div className="flex w-full items-center justify-center">
                <Link
                  href={linkUrl}
                  className="typography-button-md focus-visible:ring-offset-current group relative flex h-12 min-w-[200px] max-w-[504px] items-center justify-center gap-1 self-stretch whitespace-nowrap rounded-button-shape bg-primary p-4 text-on-primary-surface-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-accent4-base focus-visible:ring-offset-1 active:opacity-pressed"
                >
                  <span className="inline-flex items-center justify-center transition-all">
                    <span
                      data-testid="left-spacer"
                      aria-hidden="true"
                      className="inline-block w-4 align-middle transition-all group-hover:w-0"
                    />
                    <span className="inline-flex items-center">
                      {buttonCtaText}
                    </span>
                    <ArrowIcon
                      className="inline-block -translate-x-1 opacity-0 invert transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100"
                      containerClassName="inline-flex h-4 w-4 items-center justify-center align-middle"
                    />
                  </span>
                </Link>
              </div>
            </div>
          )}
          {!buttonCtaText && linkUrl && linkText && (
            <div className="flex h-10 w-full flex-grow flex-col items-center justify-center">
              <TextLink
                href={linkUrl}
                showArrow={true}
                className="flex items-center"
              >
                {linkText}
              </TextLink>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
